import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Calculator, Upload, GripVertical, Trash2 } from 'lucide-react';
import CustomerSelectorField from './CustomerSelectorField';
import SalespersonField from './SalespersonField';
import {
  createAccountRecurringInvoice,
  updateAccountRecurringInvoice,
  getAccountRecurringInvoiceById,
  getAccountRecurringInvoiceItems,
  createAccountRecurringInvoiceItem,
  deleteAccountRecurringInvoiceItem,
  uploadMenuFiles,
  deleteMenuFile,
} from '../core/_requests';
import { useAccountCustomers } from '../core/useAccountCustomers';
import { setupAxios } from '../../auth';
import SearchableSelect from '../../../shared/components/SearchableSelect'
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

type UploadedFile = {
  originalName: string;
  fileName: string;
  size: number;
  mimeType?: string;
  path: string;
  url?: string;
};

export default function AccountsNewRecurringInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const invoiceId = id ? Number(id) : null;
  const isEditing = invoiceId !== null && !Number.isNaN(invoiceId);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);

  // Hook for customers
  const customersHook = useAccountCustomers();

  // Form State
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [profileName, setProfileName] = useState('');
  const [repeatEvery, setRepeatEvery] = useState('Weekly');
  const [paymentTerms, setPaymentTerms] = useState('Due on Receipt');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10)); // Use YYYY-MM-DD
  const [endDate, setEndDate] = useState('');
  const [salesperson, setSalesperson] = useState('');

  // Line Items
  const [items, setItems] = useState<any[]>([
    { id: 1, description: '', quantity: '1.00', rate: '0.00' }
  ]);

  // Totals & Settings
  const [customerNotes, setCustomerNotes] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [discountPercent, setDiscountPercent] = useState('0');
  const [taxType, setTaxType] = useState('TDS');
  const [taxRate, setTaxRate] = useState('');
  const [adjustment, setAdjustment] = useState('0');
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const buildUploadedFiles = (raw: any): UploadedFile[] => {
    const entries = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    return entries
      .map((entry) => (typeof entry === 'string' ? entry.replace(/\\/g, '/').trim() : ''))
      .filter(Boolean)
      .map((entry) => {
        const fileName = entry.split('/').pop() || entry;
        let url = entry;
        if (!/^https?:\/\//i.test(entry)) {
          const basePath = entry.startsWith('/') ? entry : `/${entry}`;
          const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
          url = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return {
          originalName: fileName,
          fileName,
          size: 0,
          mimeType: '',
          path: entry,
          url,
        } as UploadedFile;
      });
  };

  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Load existing recurring invoice for edit mode
  useEffect(() => {
    if (!isEditing || invoiceId == null) return;
    let isActive = true;
    setLoadingInvoice(true);
    Promise.allSettled([getAccountRecurringInvoiceById(invoiceId), getAccountRecurringInvoiceItems(invoiceId)])
      .then(([invoiceRes, itemsRes]) => {
        if (!isActive) return;
        if (invoiceRes.status === 'fulfilled') {
          const data = invoiceRes.value;
          setSelectedCustomerId(data.customerId ? String(data.customerId) : '');
          setSelectedCustomerName(data.customerName || '');
          setProfileName(data.profileName || '');
          setRepeatEvery(data.frequency || 'Weekly');
          setStartDate(data.startDate || '');
          setEndDate(data.endDate || '');
          
          const filePathValue = data.filePath ?? data.filePaths ?? data.file_path ?? data.filepath ?? '';
          const existingFiles = buildUploadedFiles(filePathValue);
          setUploadedFiles(existingFiles);
        }
        if (itemsRes.status === 'fulfilled' && itemsRes.value && itemsRes.value.length > 0) {
          const rawItems = itemsRes.value;
          const sorted = [...rawItems].sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0));
          setItems(sorted.map((item: any) => ({
            id: item.id,
            description: item.description || item.itemName || '',
            quantity: item.quantity ? String(item.quantity) : '1.00',
            rate: item.rate ? String(item.rate) : '0.00'
          })));
        }
      })
      .catch(console.error)
      .finally(() => {
        if (isActive) setLoadingInvoice(false);
      });
    return () => { isActive = false; };
  }, [isEditing, invoiceId]);

  // Calculations
  const toNumber = (val: any) => {
    const num = parseFloat(val);
    return Number.isFinite(num) ? num : 0;
  };

  const getRowAmount = (row: any) => toNumber(row.quantity) * toNumber(row.rate);
  const subTotal = items.reduce((sum, row) => sum + getRowAmount(row), 0);
  const discountAmount = subTotal * (toNumber(discountPercent) / 100);
  const adjustmentAmount = toNumber(adjustment);
  const taxableBase = Math.max(0, subTotal - discountAmount + adjustmentAmount);

  const activeTaxOptions = taxType === 'TDS' ? [
    { label: 'Dividend [10%]', rate: 10 },
    { label: 'Other Interest [10%]', rate: 10 },
    { label: 'Contractors [2%]', rate: 2 },
  ] : [
    { label: 'TCS on Goods [0.1%]', rate: 0.1 }
  ];

  const selectedTax = activeTaxOptions.find(opt => opt.rate === Number(taxRate));
  const taxAmountRaw = taxableBase * ((selectedTax?.rate || 0) / 100);
  const taxAmount = taxType === 'TDS' ? -taxAmountRaw : taxAmountRaw;
  const total = taxableBase + taxAmount;

  // Build Payload
  const buildPayload = (status: string) => {
    const uploadedPaths = uploadedFiles.map((file) => file.path).filter(Boolean);
    const cleanedItems = items
      .filter(row => row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1)
      .map((row, index) => buildLineItemPayload(row, index + 1));

    return {
      customerId: selectedCustomerId ? Number(selectedCustomerId) : null,
      customerName: selectedCustomerName,
      profileName: profileName,
      frequency: repeatEvery,
      startDate: startDate || null,
      endDate: endDate || null,
      nextInvoiceDate: startDate || null, // default next to start date
      filePath: uploadedPaths.length ? uploadedPaths.join(',') : null,
      total: total,
      status: status,
      lineItems: cleanedItems
    };
  };

  const buildLineItemPayload = (row: any, index: number) => ({
    itemOrder: index,
    itemName: row.description.trim() || null,
    description: row.description.trim() || null,
    quantity: toNumber(row.quantity),
    rate: toNumber(row.rate),
    amount: getRowAmount(row)
  });

  const syncLineItems = async (targetId: number) => {
    let existing: any[] = [];
    try {
      if (isEditing) {
        existing = await getAccountRecurringInvoiceItems(targetId);
      }
    } catch (err) { console.error('Failed to get existing items', err); }
    if (existing.length > 0) {
      await Promise.all(existing.map((item: any) => deleteAccountRecurringInvoiceItem(targetId, item.id)));
    }
    const cleaned = items
      .filter(row => row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1)
      .map((row, index) => buildLineItemPayload(row, index + 1));
    if (cleaned.length > 0) {
      await Promise.all(cleaned.map(item => createAccountRecurringInvoiceItem(targetId, item)));
    }
  };

  const handleSave = async (status = 'Draft') => {
    if (!profileName.trim()) {
      alert("Please enter a profile name");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(status);
      let targetId = invoiceId;
      if (isEditing && targetId) {
        await updateAccountRecurringInvoice(targetId, payload);
      } else {
        const created = await createAccountRecurringInvoice(payload);
        targetId = created.id;
      }
      if (targetId) {
        await syncLineItems(targetId);
      }
      navigate('/accounts/sales/recurring-invoices');
    } catch (err: any) {
      console.error(err);
      alert('Failed to save recurring invoice');
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    setItems((prev) => {
      const nextId = prev.reduce((max, row) => Math.max(max, row.id), 0) + 1;
      return [...prev, { id: nextId, description: '', quantity: '1.00', rate: '0.00' }];
    });
  };

  const updateRow = (id: number, field: string, value: any) => {
    setItems(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removeRow = (id: number) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(row => row.id !== id));
    }
  };

  const triggerDocumentSelect = () => {
    documentInputRef.current?.click();
  };

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [] as File[]);
    if (!files.length) {
      return;
    }

    setUploadingFiles(true);
    setUploadError(null);

    try {
      const uploaded = await uploadMenuFiles('recurring_invoices', files);
      const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
      const formattedUploads = (uploaded as UploadedFile[]).map((f) => {
        let fileUrl = f.url || f.path;
        if (!/^https?:\/\//i.test(fileUrl)) {
          const basePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return { ...f, url: fileUrl };
      });
      setUploadedFiles((prev) => [...prev, ...formattedUploads]);
    } catch (err) {
      const message = 'Upload failed.';
      setUploadError(message);
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      if (file.fileName) {
        await deleteMenuFile('recurring_invoices', file.fileName);
      }
      setUploadedFiles((prev) => prev.filter((f) => f.path !== file.path));
    } catch (err) {
      const message = 'Delete failed.';
      setUploadError(message);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";
  const labelClass = "block text-[11px] font-bold text-slate-600 mb-1.5";

  if (loadingInvoice) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading profile...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">

              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts')}>ACCOUNTS</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts/sales')}>SALES</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-blue-600">RECURRING INVOICES</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">
                    {isEditing ? `Edit Recurring Invoice: ${profileName}` : 'New Recurring Invoice'}
                  </h1>
                  <p className="text-sm font-medium text-slate-500 mt-1">Create or edit a recurring invoice profile</p>
                </div>
              </div>

              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8">

                {/* Primary Details Section */}
                <div className="mb-8 max-w-4xl space-y-6">
                  {/* Customer Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <CustomerSelectorField
                        value={selectedCustomerId}
                        onChange={(id, name) => { setSelectedCustomerId(id); setSelectedCustomerName(name); }}
                        customersHook={customersHook}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Profile Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Profile Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="text" value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Enter profile name" className={inputClass} />
                    </div>
                  </div>

                  {/* Repeat Every */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Repeat Every</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={repeatEvery} onChange={(e) => setRepeatEvery(e.target.value)} className={inputClass}>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Yearly">Yearly</option>
                        <option value="Custom">Custom</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Payment Terms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Payment Terms</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={inputClass}>
                        <option value="Due on Receipt">Due on Receipt</option>
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 45">Net 45</option>
                        <option value="Net 60">Net 60</option>
                        <option value="Due end of the month">Due end of the month</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Start On */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Start On</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="date" placeholder="dd/MM/yyyy" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Ends On */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Ends On</label>
                    </div>
                    <div className="flex-1">
                      <input type="date" placeholder="dd/MM/yyyy" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Salesperson */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Salesperson</label>
                    </div>
                    <div className="flex-1">
                      <SalespersonField value={salesperson} onChange={setSalesperson} className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* Item Table */}
                <div className="mb-8 overflow-visible border border-slate-200 rounded-xl shadow-sm">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between rounded-t-xl">
                    <h3 className="text-sm font-bold text-slate-800">Item Table</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-175">
                      <thead>
                        <tr className="bg-white border-b border-slate-100">
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[45%]">Item Details</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Quantity</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[20%]">Rate <Calculator className="w-3 h-3 inline-block ml-1 opacity-50" /></th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Amount</th>
                          <th className="px-4 py-3 w-[5%]"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((row) => (
                          <tr key={row.id} className="bg-white group hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-3">
                                <GripVertical className="w-4 h-4 text-slate-300 mt-2 cursor-grab" />
                                <textarea
                                  rows={1}
                                  value={row.description}
                                  onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                                  placeholder="Type or click to select an item."
                                  className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all resize-none placeholder:text-slate-400"
                                ></textarea>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input
                                type="text"
                                value={row.quantity}
                                onChange={(e) => updateRow(row.id, 'quantity', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input
                                type="text"
                                value={row.rate}
                                onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top text-right">
                              <div className="pt-2 text-sm font-bold text-slate-700">
                                {getRowAmount(row).toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top text-center">
                              {items.length > 1 && (
                                <button type="button" onClick={() => removeRow(row.id)} className="p-1.5 mt-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2 rounded-b-xl">
                    <button type="button" onClick={addRow} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-100/50 hover:bg-blue-100 px-3 py-2 rounded-xl transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add New Row
                    </button>
                  </div>
                </div>

                {/* Additional Details & Totals */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <div className="lg:col-span-7 space-y-6">
                    <div>
                      <label className={labelClass}>Customer Notes</label>
                      <textarea
                        rows={3}
                        value={customerNotes}
                        onChange={(e) => setCustomerNotes(e.target.value)}
                        placeholder="Will be displayed on the recurring invoice"
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Terms & Conditions</label>
                      <textarea
                        rows={3}
                        value={termsConditions}
                        onChange={(e) => setTermsConditions(e.target.value)}
                        placeholder="Enter the terms and conditions"
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                     <div>
                      <label className={labelClass}>Attach File(s)</label>
                      <input ref={documentInputRef} type="file" multiple hidden onChange={handleDocumentChange} />
                      <button
                        type="button"
                        onClick={triggerDocumentSelect}
                        disabled={uploadingFiles}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4 text-slate-400" /> {uploadingFiles ? 'Uploading...' : 'Upload File'}
                      </button>
                      <p className="text-[11px] text-slate-400 mt-2 font-medium">You can upload a maximum of 5 files, 10MB each</p>
                      {uploadError && <p className="text-[11px] text-red-500 mt-1 font-medium">{uploadError}</p>}
                      {uploadedFiles.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {uploadedFiles.map((file) => (
                            <li key={`${file.fileName}-${file.path || ''}`} className="flex items-center gap-2 group">
                              <a href={file.url || file.path} target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                {file.originalName || file.fileName || file.path}
                              </a>
                              <button
                                type="button"
                                onClick={() => handleDeleteFile(file)}
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete file"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-5">
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold text-slate-700">Sub Total</span>
                        <span className="text-sm font-bold text-slate-900">{subTotal.toFixed(2)}</span>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-slate-500 w-24">Discount</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <div className="flex items-center w-24 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-50 transition-all">
                            <input type="text" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} className="w-full text-right px-2 py-1.5 text-sm outline-none" />
                            <div className="px-2 py-1.5 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500">%</div>
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{discountAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3 w-32">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="taxType" checked={taxType === 'TDS'} onChange={() => { setTaxType('TDS'); setTaxRate(''); }} className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300" />
                            <span className="text-[12px] font-semibold text-slate-600">TDS</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="taxType" checked={taxType === 'TCS'} onChange={() => { setTaxType('TCS'); setTaxRate(''); }} className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300" />
                            <span className="text-[12px] font-semibold text-slate-600">TCS</span>
                          </label>
                        </div>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <SearchableSelect value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-40 px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 transition-all">
                            {activeTaxOptions.length > 0 ? (
                              <>
                                <option value="">Select a Tax</option>
                                {activeTaxOptions.map(opt => (
                                  <option key={opt.label} value={String(opt.rate)}>{opt.label}</option>
                                ))}
                              </>
                            ) : (
                              <option value="" disabled>No results found</option>
                            )}
                          </SearchableSelect>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{taxAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-6">
                        <span className="text-sm font-semibold text-slate-500 w-24">Adjustment</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <input type="text" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} className="w-24 text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all" />
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{adjustmentAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                        <span className="text-lg font-black text-slate-900">Total ($)</span>
                        <span className="text-xl font-black text-blue-600">{total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-8 mt-8 border-t border-slate-100 flex items-center gap-3">
                  <button type="button" onClick={() => handleSave('Draft')} disabled={saving} className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:shadow transition-all hover:bg-slate-50 disabled:opacity-50">
                    Save as Draft
                  </button>
                  <button type="button" onClick={() => handleSave('Active')} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save and Activate'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

